<h2 class="form-title">{{ titleName }}</h2>

@if (isLoading) {
<div class="loading">
  <app-loading-indicator />
</div>
}

@if (showStatusAnnotationDialog()) {
  <div class="supervisor-dialog-backdrop"></div>
  <dialog class="supervisor-dialog bg-white" open>
    <h2 class="form-title">{{ statusAnnotationDialogTitle() }}</h2>
    <p class="mb-2">
      Do you want to include a graph annotation with a reason for this status
      change?
    </p>
    <div class="control">
      <label for="statusAnnotationText">Reason:</label>
      <input
        class="form-control"
        type="text"
        id="statusAnnotationText"
        name="statusAnnotationText"
        [ngModel]="statusAnnotationText()"
        (ngModelChange)="statusAnnotationText.set($event)"
        [maxlength]="statusAnnotationMaxLength"
        required
        [disabled]="loading"
      />
      <small
        >Required if you choose Yes. Max
        {{ statusAnnotationMaxLength }} characters.
        {{ (statusAnnotationText() || '').length }}/{{
          statusAnnotationMaxLength
        }}</small
      >
    </div>
    <p class="actions d-flex justify-content-between mt-3">
      <button
        class="w-100 mx-2"
        type="button"
        (click)="confirmStatusAnnotationDialog()"
        [disabled]="loading || !(statusAnnotationText() || '').trim()"
      >
        Yes
      </button>
      <button
        class="w-100 mx-2"
        type="button"
        (click)="cancelStatusAnnotationDialog()"
        [disabled]="loading"
      >
        No
      </button>
    </p>
  </dialog>
}

<form (ngSubmit)="onSubmit()" [class.disabled]="isLoading" id="addProgramForm">
  <div class="control" id="addProgramName">
    <label for="name">Name</label>
    <input
      class="form-control"
      type="text"
      id="name"
      name="name"
      [(ngModel)]="enteredName"
    />
  </div>

  <div class="control" id="addProgramColor">
    <label for="programColor">Color</label>
    <div class="d-flex align-items-center flex-wrap gap-2">
      @for (swatch of colorPalette; track swatch) {
        <button
          type="button"
          class="btn p-0"
          style="width: 28px; height: 28px; border-radius: 50%"
          [style.background-color]="swatch"
          [style.outline]="selectedColor() === swatch ? '3px solid #1c355e' : null"
          [title]="swatch"
          [attr.aria-label]="'Use color ' + swatch"
          (click)="selectedColor.set(swatch)"
        ></button>
      }
      <input
        class="form-control form-control-color"
        type="color"
        id="programColor"
        name="programColor"
        title="Pick a custom color"
        [ngModel]="selectedColor() ?? '#e67e22'"
        (ngModelChange)="selectedColor.set($event)"
      />
      @if (selectedColor()) {
        <button
          type="button"
          class="btn btn-link p-0"
          id="removeProgramColorButton"
          (click)="selectedColor.set(null)"
        >
          Remove color
        </button>
      } @else {
        <span>No color</span>
      }
    </div>
  </div>

  <div class="control" id="addProgramPattern">
    <label for="programPattern">Pattern</label>
    <div class="d-flex align-items-center gap-2">
      <p-dropdown
        autoWidth="false"
        [style]="{ width: '100%' }"
        appendTo="body"
        [options]="colorPatternOptions"
        [ngModel]="selectedPattern()"
        (ngModelChange)="selectedPattern.set($event ?? null)"
        name="programPattern"
        [showClear]="true"
        optionLabel="label"
        optionValue="value"
        placeholder="Solid (no pattern)"
        id="programPattern"
      />
      @if (selectedColor() || selectedPattern()) {
        <span
          style="display: inline-block; min-width: 60px; height: 28px; border-radius: 6px"
          [ngStyle]="colorPatternStyle(selectedColor(), selectedPattern())"
          title="Preview"
        ></span>
      }
    </div>
  </div>

  <div class="control-row">
    <div class="control no-margin">
      <label for="selectedProgram">Targets:</label>

      @if (isLoadingTargets) {
      <div class="targets-loading-state">
        Loading latest targets...
      </div>
      }
      @if (targetsLoadError) {
      <div class="targets-loading-error">
        {{ targetsLoadError }}
        <button
          type="button"
          class="btn btn-link p-0 ms-2"
          (click)="refreshTargets()"
        >
          Retry
        </button>
      </div>
      }

      <p-pickList
        *ngIf="!isLoading && targets && selectedTargets"
        class="program-target-picklist show-available-targets"
        [source]="targets"
        [target]="selectedTargets()"
        dataKey="ID"
        [dragdrop]="true"
        [disabled]="isLoadingTargets"
        [scrollHeight]="'24rem'"
        [sourceStyle]="{ height: '100%' }"
        [targetStyle]="{ height: '100%' }"
        [style]="{ height: '500px' }"
        sourceHeader="Available Targets"
        targetHeader="Selected Targets"
        filterBy="TargetName"
        sourceFilterPlaceholder="Filter by Target Name"
        showTargetFilter="false"
        showSourceControls="false"
        showTargetControls="false"
        controlsPosition="top"
        (onMoveToTarget)="onTargetsMovedToProgram($event.items)"
        (onMoveAllToTarget)="onTargetsMovedToProgram($event.items)"
        (onMoveToSource)="onTargetsRemovedFromProgram($event.items)"
        (onMoveAllToSource)="onTargetsRemovedFromProgram($event.items)"
      >
        <ng-template let-targetItem pTemplate="item">
          <div>
            <div class="d-flex align-items-center gap-2">
              <div>
                <div class="picklist-label">Target Name (Type)</div>
                <div class="d-flex align-items-center gap-2">
                  <div class="d-block">
                    {{
                      targetItem.TargetName +
                        " (" +
                        getTargetTypeLabel(targetItem.TargetType) +
                        ")"
                    }}
                  </div>
                  @if (isSelectedTargetSubtarget(targetItem)) {
                  <p-tag
                    severity="warning"
                    value="Sub-target"
                    class="subtarget-program-warning-badge"
                    role="button"
                    tabindex="0"
                    aria-label="Show sub-target warning details"
                    [pTooltip]="subtargetProgramWarningTooltip"
                    tooltipPosition="top"
                    (click)="toggleSubtargetProgramWarning($event, targetItem)"
                    (keydown.enter)="
                      toggleSubtargetProgramWarning($event, targetItem)
                    "
                    (keydown.space)="
                      toggleSubtargetProgramWarning($event, targetItem)
                    "
                  />
                  }
                </div>
              </div>
            </div>
          </div>
        </ng-template>
      </p-pickList>

      <p-overlayPanel
        #subtargetProgramWarningOp
        [showCloseIcon]="true"
        [dismissable]="true"
        [style]="{ width: '22rem' }"
        styleClass="subtarget-program-warning-panel"
      >
        <div class="subtarget-program-warning-content">
          <p class="mb-2">
            This target will not appear for use in the program because it is a sub-target, it only appears under it's parent target.
            Remove it from its parent if you want to use it individually.
          </p>
          @if (subtargetWarningTarget) {
            @if (getParentTargetFor(subtargetWarningTarget); as parentTarget) {
            <button
              type="button"
              class="btn btn-link p-0 subtarget-program-warning-link"
              (click)="editParentTargetFromWarning($event)"
            >
              Edit parent: {{ parentTarget.TargetName }}
            </button>
            }
          }
        </div>
      </p-overlayPanel>
    </div>
  </div>

  <div style="padding-top: 10px; width: 100%" id="addProgramAddTarget">
    <button
      id="addTargetButton"
      style="width: 100%"
      class="side-menu-button2"
      (click)="addTarget($event)"
      [disabled]="loading"
    >
      Add New Target
    </button>
  </div>

  <div class="control" id="addProgramStatus">
    <label for="selectedStatus">Status:</label>
    <p-dropdown
      autoWidth="false"
      [style]="{ width: '100%' }"
      appendTo="body"
      [options]="statuses"
      [(ngModel)]="selectedStatus"
      name="selectedStatus"
      [checkmark]="true"
      [showClear]="true"
      optionLabel="text"
      optionValue="value"
      placeholder="Select a Status"
      (onClick)="onSelectedStatusDropdownClick()"
      id="addProgramStatusDropdown"
    />
  </div>

  @if (shouldShowStatusAnnotationEditor()) {
  <div class="control" id="addProgramStatusAnnotation">
    <label for="editStatusAnnotationText">Status Annotation:</label>
    <input
      class="form-control"
      type="text"
      id="editStatusAnnotationText"
      name="editStatusAnnotationText"
      [ngModel]="statusAnnotationText()"
      (ngModelChange)="statusAnnotationText.set($event)"
      [maxlength]="statusAnnotationMaxLength"
      [disabled]="loading"
    />
    <small
      >Graph annotation reason (max {{ statusAnnotationMaxLength }} characters).
      {{ (statusAnnotationText() || '').length }}/{{
        statusAnnotationMaxLength
      }}</small
    >
  </div>
  }

  <div class="control" id="addProgramSkillArea">
    <label for="selectedSkillArea">Skill Area:</label>
    <p-dropdown
      autoWidth="false"
      [style]="{ width: '100%' }"
      appendTo="body"
      [options]="skillAreas"
      [(ngModel)]="selectedSkillArea"
      name="selectedSkillArea"
      [checkmark]="true"
      optionLabel="SkillAreaName"
      [showClear]="true"
      placeholder="Select a Skill Area"
      (onClick)="onSelectedSkillAreaDropdownClick()"
      id="addProgramSkillAreaDropdown"
    />
  </div>

  <div class="control" id="addProgramHiddenFromRoles">
    <label for="addProgramHiddenFromRolesDropdown"
      >Hide from roles:
      <i
        class="pi pi-info-circle"
        pTooltip="Users whose roles are all selected won't see this program in Spark."
        tooltipPosition="top"
      ></i>
    </label>
    <p-multiSelect
      id="addProgramHiddenFromRolesDropdown"
      appendTo="body"
      [style]="{ width: '100%' }"
      [options]="roles"
      [(ngModel)]="selectedHiddenFromRoleIDs"
      name="selectedHiddenFromRoleIDs"
      placeholder="Visible to all roles"
      optionLabel="Name"
      optionValue="ID"
      [filter]="true"
      filterBy="Name"
      display="chip"
      [showClear]="true"
    />
  </div>


  <div class="control" id="addProgramSkillAcquisitionGoal">
    <label for="selectedSkillAcquisitionGoal">{{ academyModeService.getCurrentObjectiveLabel() }}</label>
    <p-dropdown
      class="ProgramSkillAcquisitionGoal-dropdown"
      autoWidth="false"
      [style]="{ width: '100%' }"
      [panelStyle]="{ maxWidth: '750px' }"
      appendTo="body"
      [options]="skillAcquisitionGoalOptions"
      [ngModel]="selectedSkillAcquisitionGoalValue()"
      (ngModelChange)="onSkillAcquisitionGoalChange($event)"
      name="selectedSkillAcquisitionGoal"
      [checkmark]="true"
      optionLabel="DropdownLabel"
      optionValue="DropdownValue"
      [showClear]="true"
      [placeholder]="academyModeService.getCurrentObjectivePlaceholder()"
      (onClick)="onSelectedSkillAcquisitionGoalDropdownClick()"
      id="addProgramSkillAcquisitionGoalDropdown"
    >
      <ng-template let-skillAcquisitionGoalItem pTemplate="item">
        <div class="d-flex align-items-center gap-2">
          <div>
            <div class="d-block wrap-text">
              {{ skillAcquisitionGoalItem.DropdownLabel }}
            </div>
          </div>
        </div>
      </ng-template>
    </p-dropdown>
  </div>

  <div
    class="control"
    *ngIf="selectedSkillAcquisitionGoal() && !isOtherSkillAcquisitionGoalSelected()"
  >
    <label>Objective Details:</label>
    <div class="objective-details">
      <ng-container *ngIf="selectedSkillAcquisitionGoal()?.SkillArea">
        <div class="objective-details-label">Skill Area</div>
        <div class="objective-details-value">
          {{ selectedSkillAcquisitionGoal()?.SkillArea }}
        </div>
      </ng-container>
      <ng-container *ngIf="selectedSkillAcquisitionGoal()?.Program">
        <div class="objective-details-label">Program</div>
        <div class="objective-details-value">
          {{ selectedSkillAcquisitionGoal()?.Program }}
        </div>
      </ng-container>
      <ng-container *ngIf="selectedSkillAcquisitionGoal()?.GoalStatus">
        <div class="objective-details-label">Objective Status</div>
        <div class="objective-details-value">
          {{ selectedSkillAcquisitionGoal()?.GoalStatus }}
        </div>
      </ng-container>
      <ng-container *ngIf="selectedSkillAcquisitionGoal()?.Goal">
        <div class="objective-details-label">Objective</div>
        <div class="objective-details-value">
          {{ selectedSkillAcquisitionGoal()?.Goal }}
        </div>
      </ng-container>
      <ng-container *ngIf="selectedSkillAcquisitionGoal()?.BaselineDate">
        <div class="objective-details-label">Baseline Date</div>
        <div class="objective-details-value">
          {{
            selectedSkillAcquisitionGoal()?.BaselineDate | date : "MM/dd/yyyy"
          }}
        </div>
      </ng-container>
      <ng-container *ngIf="selectedSkillAcquisitionGoal()?.Baseline">
        <div class="objective-details-label">Baseline</div>
        <div class="objective-details-value">
          {{ selectedSkillAcquisitionGoal()?.Baseline }}
        </div>
      </ng-container>
      <ng-container
        *ngIf="selectedSkillAcquisitionGoal()?.SociallySignificantInformation"
      >
        <div class="objective-details-label">Socially Significant Info</div>
        <div class="objective-details-value">
          {{ selectedSkillAcquisitionGoal()?.SociallySignificantInformation }}
        </div>
      </ng-container>
      <ng-container *ngIf="selectedSkillAcquisitionGoal()?.Progress">
        <div class="objective-details-label">Progress</div>
        <div class="objective-details-value">
          {{ selectedSkillAcquisitionGoal()?.Progress }}
        </div>
      </ng-container>
      <ng-container *ngIf="selectedSkillAcquisitionGoal()?.PreviousGoal">
        <div class="objective-details-label">Previous Objective</div>
        <div class="objective-details-value">
          {{ selectedSkillAcquisitionGoal()?.PreviousGoal }}
        </div>
      </ng-container>
    </div>
  </div>

  <div
    class="control"
    *ngIf="
      selectedSkillAcquisitionGoal() &&
      !isOtherSkillAcquisitionGoalSelected() &&
      selectedSkillAcquisitionGoal()?.TreatmentGoals &&
      (selectedSkillAcquisitionGoal()?.TreatmentGoals?.length ?? 0) > 0
    "
  >
    <label for="selectedOtherCurrentObjective"
      >Treatment Goals that support this objective:</label
    >
    @for (goal of selectedSkillAcquisitionGoal()?.TreatmentGoals; track goal) {
    <div class="ms-3 d-flex align-items-center gap-2">
      <div>
        <div class="d-block mb-2 wrap-text text-white text-14">
          - {{ goal }}
        </div>
      </div>
    </div>
    }
  </div>

  <div
    class="control"
    id="addProgramOtherCurrentObjective"
    *ngIf="isOtherSkillAcquisitionGoalSelected()"
  >
    <label for="selectedOtherCurrentObjective">Other Current Objective:</label>
    <textarea
      class="form-control"
      id="otherCurrentObjective"
      name="otherCurrentObjective"
      [(ngModel)]="enteredOtherCurrentObjective"
    ></textarea>
  </div>

  <div class="control" id="addProgramDetails">
    <label for="details">Program Instructions</label>

    <div
      class="editor-container editor-container_classic-editor"
      #editorContainerElement
    >
      <div class="editor-container__editor">
        <div #editorElement>
          <ckeditor
            id="details"
            name="details"
            [editor]="Editor"
            [config]="editorConfig"
            [data]="programToEdit?.Details ?? ''"
            (change)="onDetailsChange($event)"
          ></ckeditor>
        </div>
      </div>
    </div>
  </div>

  <div id="dates">
    <div class="control-row">
      <div class="control no-margin">
        <label for="DateOpened">Date Opened</label>
        <input
          type="date"
          [style]="{ width: '100%' }"
          appendTo="body"
          class="form-control"
          name="DateOpened"
          [(ngModel)]="dateOpened"
          autocomplete="off"
        />
      </div>

      <div class="control no-margin">
        <label for="DateBaselined">Date Probed</label>
        <input
          type="date"
          [style]="{ width: '100%' }"
          appendTo="body"
          class="form-control"
          name="DateBaselined"
          [(ngModel)]="dateBaselined"
          autocomplete="off"
        />
      </div>
    </div>

    <div class="control-row">
      <div class="control no-margin">
        <label for="DateMastered">Date Mastered</label>
        <input
          type="date"
          [style]="{ width: '100%' }"
          appendTo="body"
          class="form-control"
          name="DateMastered"
          [(ngModel)]="dateMastered"
          autocomplete="off"
        />
      </div>

      <div class="control no-margin">
        <label for="DateClosed">Date Closed</label>
        <input
          type="date"
          [style]="{ width: '100%' }"
          appendTo="body"
          class="form-control form-control"
          name="dateClosed"
          [(ngModel)]="dateClosed"
          autocomplete="off"
        />
      </div>
    </div>
  </div>

  <p class="actions d-flex justify-content-between" id="addProgramActions">
    <button
      class="w-100 mx-2"
      type="button"
      (click)="onCancel()"
      style="margin-right: auto"
    >
      Cancel
    </button>
    @if (programToEdit) {
    <button
      class="w-100 mx-2"
      type="button"
      id="archiveButton"
      [disabled]="loading"
      (click)="archiveButtonClicked()"
    >
      {{ programToEdit!.IsArchived ? "Unarchive" : "Archive" }}
    </button>
    }
    <button
      class="w-100 mx-2"
      type="submit"
      id="saveProgramButton"
      [disabled]="loading"
    >
      {{ actionName }}
    </button>
  </p>
</form>
